import { Fragment } from 'react';

import { useCartOfferValidation } from 'features/cart/hooks/use-cart-offer-validation';
import { commonStyles } from 'features/cart/styles';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl';
import { Icon } from 'ucl/components/icon/Icon';

import { Skeleton } from 'components/ui/skeleton';
import { WalmartBadgeContainer } from 'pages/cart/cart-item/cart-item-offer';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';

import { CartEntryFabButton } from '../cart-actions/cart-entry-fab-button';

const IncentiveHeaderOfferFeedback = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const { feedbackMessages, isOfferApplied, isLoading } = useCartOfferValidation(cartEntry);

  const offerMessageKey = isOfferApplied ? 'offerApplied' : 'offerNotApplied';
  const offerMessage = t(offerMessageKey);
  const textSeparator = ' - ';

  if (isLoading) {
    return <Skeleton shape="rectangle" style={{ width: '80%', height: 12, marginVertical: 0 }} />;
  }

  return (
    <View style={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
      <Icon marginRight={1} variant={isOfferApplied ? 'checkFill' : 'errorV2'} size="3" />
      <Typography style={styles.feedbackText}>
        {offerMessage}
        {feedbackMessages?.map((message, index) => (
          <Fragment key={index}>
            {textSeparator}
            {message}
          </Fragment>
        ))}
      </Typography>
    </View>
  );
};

const CartEntryIncentiveHeader = ({
  cartEntry,
  isReadOnly,
}: {
  cartEntry: ICartEntry;
  isReadOnly: boolean;
}) => {
  const { isOfferApplied, isLoading, walmart } = useCartOfferValidation(cartEntry);

  let headerBackgroundColor: string;
  if (isLoading) {
    headerBackgroundColor = primitive.$blackOpacity10;
  } else {
    headerBackgroundColor = isOfferApplied ? primitive.$successLight : primitive.$errorLight;
  }

  if (isReadOnly) {
    return (
      <View style={styles.header}>
        <Typography style={commonStyles.cartEntryItemName}>{cartEntry.name}</Typography>
      </View>
    );
  }

  return (
    <View
      style={[
        styles.header,
        {
          gap: 2,
          flexDirection: 'column',
          alignItems: 'flex-start',
          backgroundColor: headerBackgroundColor,
        },
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        {(walmart.isOfferSelected || walmart.isLoading) && <WalmartBadgeContainer />}
        <Typography style={commonStyles.cartEntryItemName}>{cartEntry.name}</Typography>
      </View>
      <IncentiveHeaderOfferFeedback cartEntry={cartEntry} />
    </View>
  );
};

const CartEntryComboHeader = ({
  cartEntry,
  showFabButton = true,
}: {
  cartEntry: ICartEntry;
  showFabButton?: boolean;
}) => {
  return (
    <>
      {showFabButton && <CartEntryFabButton cartEntryId={cartEntry.cartId} />}
      <View style={styles.header}>
        <Typography style={commonStyles.cartEntryItemName}>{cartEntry.name}</Typography>
      </View>
    </>
  );
};

const CartEntryRewardHeader = ({ cartEntry }: { cartEntry: ICartEntry }) => {
  const rewardBenefit = cartEntry.children?.[0];
  if (!rewardBenefit) {
    return null;
  }
  if (rewardBenefit.type === MinimalCartEntryType.combo) {
    return <CartEntryComboHeader cartEntry={rewardBenefit} showFabButton={false} />;
  }
  return null;
};

export const CartEntryHeader = ({
  cartEntry,
  readOnly = false,
}: {
  cartEntry: ICartEntry;
  readOnly?: boolean;
}) => {
  if (cartEntry.type === MinimalCartEntryType.reward) {
    return <CartEntryRewardHeader cartEntry={cartEntry} />;
  }

  if (cartEntry.type === MinimalCartEntryType.combo) {
    return <CartEntryComboHeader cartEntry={cartEntry} showFabButton={!readOnly} />;
  }

  if (cartEntry.type === MinimalCartEntryType.incentive) {
    return <CartEntryIncentiveHeader cartEntry={cartEntry} isReadOnly={readOnly} />;
  }

  return null;
};

const styles = StyleSheet.create({
  header: {
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    ...commonStyles.withBorderBottom,
  },
  feedbackText: {
    fontSize: 10,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
  },
});
